import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["clearable", "extraTimes", "format", "interval", "max", "maxValue", "min", "minValue", "value", "menuWidth", "size", "use", "onChange", "placeholder"];
import { Select } from '../../../components/Select';
import unescapedText from 'I18n/utils/unescapedText';
import I18n from 'I18n';
import devLogger from 'react-utils/devLogger';
import { TimeIcon } from 'foundations-assets';
import { Controllable } from '../Controllable/Controllable';
import { toShorthandSize } from '../../utils/tShirtSizes';
import { TimePickerContext, useTimePickerContext } from './TimePickerProvider';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { useTheme } from 'foundations-theming/utils';
import { useCallback, useMemo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const MINUTES_IN_A_DAY = 24 * 60;
const NUMBER_REGEX = /\d/;
const condense = str => str.replace(/\s/g, '').toLowerCase();
const formatTime = (format, value) => I18n.moment.utc(0).minutes(value).format(format);
const generateOptions = (extraTimes, format, interval, max, min, normalizedValue) => {
  if (interval <= 0) {
    const msg = 'TimePicker: Interval cannot be a number less than or equal to 0';
    devLogger.warn({
      message: msg,
      key: msg
    });
  }
  const calculatedInterval = interval <= 0 ? 1 : interval;
  const options = [];
  const computedMin = typeof min === 'number' ? min : 0;
  const computedMax = typeof max === 'number' ? max : MINUTES_IN_A_DAY;
  for (let value = 0; value < MINUTES_IN_A_DAY; value += calculatedInterval) {
    const outOfRange = computedMin > value || value > computedMax;
    if (outOfRange) {
      continue;
    }
    const text = formatTime(format, value);
    options.push({
      text,
      value
    });
    if (normalizedValue && normalizedValue > value && normalizedValue < value + calculatedInterval) {
      const normalizedValueText = I18n.moment.utc(0).minutes(normalizedValue).format(format);
      options.push({
        text: normalizedValueText,
        value: normalizedValue
      });
    }
  }
  extraTimes.forEach(value => {
    if (!options.some(option => option.value === value)) {
      const text = formatTime(format, value);
      options.push({
        text,
        value
      });
    }
  });
  return options;
};
const normalizeValue = (format, value) => {
  // Return a string corresponding to the number of minutes into the day
  // represented by the value.
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  devLogger.warn({
    message: `TimePicker: String value "${value}" given. This can cause inconsistent behavior.`,
    key: 'TimePicker: string value'
  });
  const moment = I18n.moment.utc(value, format);
  return moment.hours() * 60 + moment.minutes();
};
const optionMatchesSearch = (option, search) => {
  var _condensedSearch$matc, _condensedOptionText$;
  if (!search) {
    return true;
  }
  const condensedSearch = condense(search);
  const condensedOptionText = condense(option.text);

  // If search contains no numbers, do exact character matching
  if (!NUMBER_REGEX.test(condensedSearch)) {
    return condensedOptionText.includes(condensedSearch);
  }

  // Check for AM/PM in search
  const searchHasAM = condensedSearch.includes('am');
  const searchHasPM = condensedSearch.includes('pm');
  const optionHasAM = condensedOptionText.includes('am');
  const optionHasPM = condensedOptionText.includes('pm');

  // If search specifies AM/PM, filter accordingly
  if (searchHasAM && !optionHasAM || searchHasPM && !optionHasPM) {
    return false;
  }

  // For numeric searches
  const searchFirstNumber = (_condensedSearch$matc = condensedSearch.match(NUMBER_REGEX)) === null || _condensedSearch$matc === void 0 ? void 0 : _condensedSearch$matc[0];
  const optionFirstNumber = (_condensedOptionText$ = condensedOptionText.match(NUMBER_REGEX)) === null || _condensedOptionText$ === void 0 ? void 0 : _condensedOptionText$[0];

  // First number must match
  if (searchFirstNumber !== optionFirstNumber) {
    return false;
  }

  // Then check if all search characters appear in sequence
  let lastIndex = -1;
  return [...condensedSearch].every(char => {
    const index = condensedOptionText.indexOf(char, lastIndex + 1);
    if (index === -1) return false;
    lastIndex = index;
    return true;
  });
};
const getSearchResults = (options, search) => {
  const filteredOptions = options.filter(option => optionMatchesSearch(option, search));
  const exactMatches = [];
  const otherMatches = [];
  filteredOptions.forEach(option => {
    if (condense(option.text).startsWith(condense(search))) {
      exactMatches.push(option);
    } else {
      otherMatches.push(option);
    }
  });
  return [...exactMatches, ...otherMatches];
};

// NOTE: This is a temporary function to get the button use for the TimePicker. It will be updated if and when we have a buttonUse prop for Select.

const getButtonUse = use => {
  if (use === 'on-dark') {
    return 'on-dark';
  }
  return 'default';
};
const getMenuWidth = (menuWidth, shorthandSize) => {
  if (menuWidth) {
    return menuWidth;
  }
  if (shorthandSize === 'sm') {
    return 300;
  }
  return undefined;
};
const TimePickerComponent = _ref => {
  var _timePickerContext$se;
  let {
      clearable = true,
      extraTimes = [],
      format = 'LT',
      interval = 30,
      max,
      maxValue,
      min,
      minValue,
      value,
      menuWidth,
      size = 'default',
      use = 'default',
      onChange,
      placeholder = unescapedText('salesUI.UITimePicker.placeholder')
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const normalizedValue = normalizeValue(format, value !== null && value !== void 0 ? value : null);
  const computedMax = max || maxValue || MINUTES_IN_A_DAY;
  const computedMin = min || minValue || 0;
  const theme = useTheme();
  const timePickerContext = useTimePickerContext();
  const baseOptions = generateOptions(extraTimes, format, interval, computedMax, computedMin, normalizedValue);
  const shorthandSize = toShorthandSize(size);
  const buttonUse = getButtonUse(use);
  const computedMenuWidth = getMenuWidth(menuWidth, shorthandSize);
  const handleSelectedOptionChange = event => {
    if (onChange) {
      onChange({
        target: {
          value: event.value === null ? null : Number(event.value)
        }
      });
    }
  };
  const selectStylesOverrides = deepMerge({
    color: theme.color['fill-secondary-default'],
    opacity: 1
  }, (_timePickerContext$se = timePickerContext === null || timePickerContext === void 0 ? void 0 : timePickerContext.selectStylesOverrides) !== null && _timePickerContext$se !== void 0 ? _timePickerContext$se : {});
  const loadAsyncOptions = useCallback(({
    input,
    callback
  }) => {
    const filtered = getSearchResults(baseOptions, input).map(option => ({
      text: option.text,
      value: String(option.value)
    }));
    callback({
      options: filtered
    });
  }, [baseOptions]);
  const selectedValues = useMemo(() => normalizedValue !== null ? [String(normalizedValue)] : [], [normalizedValue]);
  return /*#__PURE__*/_jsx(TimePickerContext.Provider, {
    value: {
      selectStylesOverrides
    },
    children: /*#__PURE__*/_jsx(Select, Object.assign({}, rest, {
      clearable: clearable,
      multi: false,
      referenceIndicator: /*#__PURE__*/_jsx(TimeIcon, {
        size: "400"
      }),
      searchable: true,
      selectedItemsPlaceholder: placeholder,
      menuInlineSize: computedMenuWidth,
      loadAsyncOptions: loadAsyncOptions,
      selectedValues: selectedValues,
      onSelectedOptionChange: handleSelectedOptionChange,
      referenceVariant: buttonUse === 'on-dark' ? 'on-dark' : 'default'
    }))
  });
};
export const TimePicker = Controllable(TimePickerComponent, ['value']);